import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ApolloProvider } from "@apollo/client";
import { AnimatePresence } from "framer-motion";
import { apolloClient, setAuthToken } from "./lib/apollo-client";
import { AuthProvider, useAuth } from "./lib/auth";
import { CompanyProvider, useCompany, useBrandName } from "./lib/company";
import { expandMiniApp, telegram } from "./lib/telegram";
import { Dashboard } from "./pages/Dashboard";
import { CardDetail } from "./pages/CardDetail";
import { Wallet } from "./pages/Wallet";
import { Deposit } from "./pages/Deposit";
import { Transactions } from "./pages/Transactions";
import { Settings } from "./pages/Settings";
import { Profile } from "./pages/Profile";
import { Referral } from "./pages/Referral";
import { Onboarding } from "./pages/Onboarding";
import { DashboardSkeleton } from "./components/SkeletonLoader";
import { BottomNav } from "./components/BottomNav";
import { BlowfishLogo } from "./components/BlowfishLogo";

const AppLayout = () => {
  const location = useLocation();
  const hideNav = location.pathname.startsWith("/card/");

  return (
    <>
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/card/:id" element={<CardDetail />} />
          <Route path="/wallet" element={<Wallet />} />
          <Route path="/deposit" element={<Deposit />} />
          <Route path="/transactions" element={<Transactions />} />
          <Route path="/referral" element={<Referral />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AnimatePresence>
      {!hideNav && <BottomNav />}
    </>
  );
};

const CompanyGate = ({ children }: { children: React.ReactNode }) => {
  const { isLoading, company, error } = useCompany();

  if (isLoading) {
    return <DashboardSkeleton />;
  }
  if (error || !company) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-6 bg-ground">
        <BlowfishLogo size="lg" />
        <div className="text-center mt-6">
          <p className="text-lg font-medium mb-2">Domain not configured</p>
          <p className="text-muted text-sm">{error || "This URL is not associated with any organization."}</p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
};

const AuthGate = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, token, needsOnboarding, error } = useAuth();
  const brandName = useBrandName();

  useEffect(() => {
    if (token) {
      setAuthToken(token);
    }
  }, [token]);

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  if (needsOnboarding) {
    return <Onboarding />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-6 bg-ground">
        <BlowfishLogo size="lg" />
        <div className="text-center mt-6">
          <p className="text-lg font-medium mb-2">Unable to connect</p>
          <p className="text-muted text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-6 bg-ground">
        <BlowfishLogo size="lg" />
        <div className="text-center mt-6">
          <p className="text-lg font-medium mb-2">Please open from Telegram</p>
          <p className="text-muted text-sm">
            This app must be opened through the {brandName} Telegram bot.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

const App = () => {
  useEffect(() => {
    expandMiniApp();
    telegram.ready();
  }, []);

  return (
    <ApolloProvider client={apolloClient}>
      <CompanyProvider>
        <CompanyGate>
          <AuthProvider>
            <BrowserRouter>
              <AuthGate>
                <AppLayout />
              </AuthGate>
            </BrowserRouter>
          </AuthProvider>
        </CompanyGate>
      </CompanyProvider>
    </ApolloProvider>
  );
};

export default App;
